import { useEffect } from 'react';
import { useTranslations } from 'next-intl';
import { GlowSkeleton, GlowTextBody, GlowTextBodySmall } from '@headless-workspace/glow-ds';
import { Component } from '@headless-workspace/typings';
import { LocalityDetails, StoreInfoValue } from '../../../../server';
import { NoStoreFound } from './NoStoreFound';
import { StoreSelectionMenuMap } from './map';
import { StoresList } from './storeDeliveryContainer/StoresList';
import { StoreRecoveryStatus } from './useStoreInfos';

const NUMBER_OF_SKELETONS = 5;

export enum DisplayOption {
    List = 'list',
    Map = 'map',
}

export type StoreSliderDisplayProps = {
    stores: StoreInfoValue[];
    storesRecoveryStatus: StoreRecoveryStatus;
    userPosition: GeolocationPosition | null;
    displayOption: DisplayOption;
    highlightedStoreId?: string;
    setHighlightedStore: (store?: StoreInfoValue) => void;
    favoriteStoreId: string;
    currentTime: string;
    localityDetails?: LocalityDetails | null;
    setIsMoreStoresVisible: (visible: boolean) => void;
    displayMoreStoresVisibleButton: boolean;
};

export const StoreSliderDisplay: Component<StoreSliderDisplayProps> = ({
    stores,
    storesRecoveryStatus,
    userPosition,
    displayOption,
    highlightedStoreId,
    setHighlightedStore,
    favoriteStoreId,
    currentTime,
    localityDetails,
    setIsMoreStoresVisible,
    displayMoreStoresVisibleButton,
}) => {
    const t = useTranslations('Modal.stores');

    useEffect(() => {
        setIsMoreStoresVisible(false);
    }, [localityDetails, setIsMoreStoresVisible]);

    if (storesRecoveryStatus === 'init' || storesRecoveryStatus === 'error') {
        return null;
    }

    if (stores.length === 0 && storesRecoveryStatus === 'success') {
        return <NoStoreFound />;
    }

    const isLoading = storesRecoveryStatus === 'loading';

    if (displayOption === DisplayOption.Map) {
        return isLoading ? (
            <GlowSkeleton
                containerClassName={'h-full w-full'}
                height={'100%'}
                width={'100%'}
                itemClassName={'!rounded-0'}
            />
        ) : (
            <StoreSelectionMenuMap
                highlightedStoreId={highlightedStoreId}
                setHighlightedStore={setHighlightedStore}
                stores={stores}
                currentUserPosition={userPosition}
                favoriteStoreId={favoriteStoreId}
                localityDetails={localityDetails}
            />
        );
    }

    return (
        <section className={'flex flex-col gap-1 h-full'} aria-labelledby={'stores-heading'}>
            <div>
                <GlowTextBody
                    id={'stores-heading'}
                    text={t('list.label.nearestStore')}
                    fontWeight={'bold'}
                    TagName={'h3'}
                />
                {currentTime && (
                    <GlowTextBodySmall
                        text={t('list.label.informationLastUpdatedTime', { currentTime })}
                        color={'tertiary'}
                    />
                )}
            </div>
            {isLoading ? (
                <GlowSkeleton height={'11.75rem'} line={NUMBER_OF_SKELETONS} containerClassName={'!gap-1'} />
            ) : (
                <StoresList
                    favoriteStoreId={favoriteStoreId}
                    stores={stores}
                    setHighlightedStore={setHighlightedStore}
                    highlightedStoreId={highlightedStoreId}
                    onMoreStoresVisibleClick={setIsMoreStoresVisible}
                    displayMoreStoresVisibleButton={displayMoreStoresVisibleButton}
                />
            )}
        </section>
    );
};
